// Env-aware public tenant-site URL builder.
//
// Detect env from the runtime host, not a build flag: `vite.config` bakes
// `process.env.NODE_ENV` from `.env` (`local`) into every build — including
// production — so build-flag checks would point previews at *.localhost:3000
// on dash.staty.io. The target follows where the admin actually runs.
// (The Vite dev server for apps/app accepts `{slug}.localhost:3000` and
// getLeagueName.ts parses the subdomain, so local previews hit the LOCAL
// public app.)
//
// The domain itself comes from the ONE shared source of truth —
// packages/shared/domains.ts. Never hardcode the tenant root here or in
// callers.

import { tenantOrigin, localTenantOrigin, orgOrigin } from '@staty/shared/domains';

export function isLocalAdminHost(): boolean {
  const { hostname } = window.location;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.localhost')
  );
}

export function publicSiteUrl(slug: string, path = '/'): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return isLocalAdminHost()
    ? `${localTenantOrigin(slug)}${normalizedPath}`
    : `${tenantOrigin(slug)}${normalizedPath}`;
}

/**
 * Org-aware "view my public site" origin for admin chrome (nav "Public
 * Site", etc.). On a LOCAL admin host the target is the local public app
 * (`http://<slug>.localhost:3000`) — a custom domain never resolves locally,
 * so orgOrigin's custom-domain preference would 404 the owner mid-testing.
 * Everywhere else it defers to orgOrigin (active custom domain first, else
 * the tenant subdomain — INT-0007).
 */
export function publicOrgOrigin(
  org: { name?: string; customDomain?: { hostname?: string; status?: string } | null } | null | undefined,
  fallbackSlug = '',
): string {
  const slug = (org && org.name) || fallbackSlug;
  if (isLocalAdminHost()) return localTenantOrigin(slug);
  return orgOrigin({ ...(org || {}), name: slug });
}
